import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useClientAuth } from '../contexts/ClientAuthContext';
import { Lock, Phone, AlertCircle, RefreshCw, Shield } from 'lucide-react';

const API = `${window.location.origin}/api`;

const ClientLogin = () => {
  const [lastSixDigits, setLastSixDigits] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [lockedUntil, setLockedUntil] = useState(null);
  const navigate = useNavigate();
  const { login, isAuthenticated } = useClientAuth();

  // Captcha state (server-issued challenge, validated server-side)
  const [captcha, setCaptcha] = useState(null);
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaError, setCaptchaError] = useState('');

  const refreshCaptcha = async (signal) => {
    setCaptchaInput('');
    setCaptchaError('');
    try {
      const response = await axios.get(`${API}/captcha/challenge`, { signal });
      setCaptcha(response.data);
    } catch (e) {
      if (!axios.isCancel(e)) setCaptcha(null);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    refreshCaptcha(controller.signal);
    return () => controller.abort();
  }, []);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/espaceclient/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Countdown timer for lockout
  useEffect(() => {
    if (lockedUntil) {
      const timer = setInterval(() => {
        const remaining = Math.ceil((lockedUntil - Date.now()) / 1000);
        if (remaining <= 0) {
          setLockedUntil(null);
          setError('');
        }
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [lockedUntil]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setCaptchaError('');

    // Check if locked
    if (lockedUntil && Date.now() < lockedUntil) {
      const remaining = Math.ceil((lockedUntil - Date.now()) / 1000);
      setError(`Compte temporairement bloqué. Réessayez dans ${remaining} secondes.`);
      return;
    }

    if (lastSixDigits.length !== 6) {
      setError('Veuillez entrer exactement 6 chiffres');
      return;
    }

    if (!/^\d{6}$/.test(lastSixDigits)) {
      setError('Seuls les chiffres sont autorisés');
      return;
    }

    // Captcha is validated server-side (one-time use challenge)
    if (!captcha || !captchaInput) {
      setCaptchaError('Veuillez répondre à la question de vérification.');
      return;
    }

    setLoading(true);

    const result = await login(lastSixDigits, parseInt(captchaInput), captcha.captcha_id);

    setLoading(false);

    if (result.success) {
      // Navigate to dashboard (email popup will show there if needed)
      navigate('/espaceclient/dashboard');
    } else {
      // Check if it's a lockout response (429)
      if (result.error && result.error.includes('Trop de tentatives') || result.error.includes('429')) {
        // Extract remaining time from error message
        const match = result.error.match(/(\d+)\s*seconde/);
        if (match) {
          const seconds = parseInt(match[1]);
          setLockedUntil(Date.now() + seconds * 1000);
        }
      }
      setError(result.error);
      refreshCaptcha();
    }
  };

  const remainingSeconds = lockedUntil ? Math.max(0, Math.ceil((lockedUntil - Date.now()) / 1000)) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo/Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Espace Client</h1>
          <p className="text-gray-600">Connectez-vous avec votre numéro de téléphone</p>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Phone Number Input */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <div className="flex items-center gap-2 mb-2">
                  <Phone className="w-4 h-4" />
                  <span>6 derniers chiffres de votre téléphone</span>
                </div>
              </label>
              <input
                type="text"
                data-testid="client-phone-input"
                value={lastSixDigits}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setLastSixDigits(value);
                }}
                placeholder="123456"
                className="w-full px-4 py-3 text-center text-2xl font-mono tracking-widest border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                maxLength="6"
                autoFocus
                required
                disabled={lockedUntil && Date.now() < lockedUntil}
              />
              <p className="text-xs text-gray-500 mt-2">
                Entrez les 6 derniers chiffres de votre numéro de téléphone
              </p>
            </div>

            {/* Captcha */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
                <Shield className="w-4 h-4 text-green-600" />
                <span>Vérifiez que vous n'êtes pas un robot</span>
              </label>
              
              <div className="flex items-center gap-3 mb-3">
                <div className="flex-1 bg-white border border-gray-300 rounded-lg px-4 py-3 text-center">
                  <span className="text-lg font-bold text-gray-800" data-testid="client-captcha-question">
                    {captcha ? captcha.num1 : '…'} + {captcha ? captcha.num2 : '…'} = ?
                  </span>
                </div>
                <button
                  type="button"
                  onClick={refreshCaptcha}
                  className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  title="Nouvelle question"
                >
                  <RefreshCw className="w-5 h-5" />
                </button>
              </div>
              
              <input
                type="text"
                data-testid="client-captcha-input"
                value={captchaInput}
                onChange={(e) => {
                  setCaptchaInput(e.target.value.replace(/\D/g, ''));
                  setCaptchaError('');
                }}
                placeholder="Votre réponse"
                className={`w-full px-4 py-2 text-center border-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  captchaError ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                disabled={lockedUntil && Date.now() < lockedUntil}
              />
              
              {captchaError && (
                <p className="text-red-600 text-sm mt-2 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {captchaError}
                </p>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Lockout Warning */}
            {lockedUntil && Date.now() < lockedUntil && (
              <div className="flex items-center gap-2 p-3 bg-orange-50 border border-orange-200 rounded-lg text-orange-700 text-sm">
                <Shield className="w-5 h-5 flex-shrink-0" />
                <span>
                  Compte temporairement bloqué. Réessayez dans <strong>{remainingSeconds}</strong> secondes.
                </span>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              data-testid="client-login-submit"
              disabled={loading || lastSixDigits.length !== 6 || !captchaInput || (lockedUntil && Date.now() < lockedUntil)}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Connexion...</span>
                </>
              ) : (
                <>
                  <Lock className="w-5 h-5" />
                  <span>Se connecter</span>
                </>
              )}
            </button>
          </form>

          {/* Help Text */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-600 text-center">
              Besoin d'aide ? Contactez l'administrateur
            </p>
          </div>
        </div>

        {/* Back to Home */}
        <div className="text-center mt-6">
          <a
            href="/"
            className="text-blue-600 hover:text-blue-700 text-sm font-medium inline-flex items-center gap-1"
          >
            ← Retour à l'accueil
          </a>
        </div>
      </div>
    </div>
  );
};

export default ClientLogin;
